import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import {
  Home, GraduationCap, Newspaper, HelpCircle, Mail, Users, Award,
  Briefcase, FileText, ScrollText, Map, BookOpen, Sparkles,
} from 'lucide-react';

/**
 * Plan du site — public sitemap-style page for SEO + UX.
 * Lists every public route grouped by intent, plus the live blog index from the API.
 */
const SECTIONS = [
  {
    title: 'Découvrir',
    icon: Home,
    links: [
      { to: '/',                  label: 'Accueil',                desc: 'Centre de formation en sellerie-garnissage à Istres.' },
      { to: '/a-propos',          label: 'Le formateur',           desc: 'Benoît Kristof — maître sellier formé chez les Compagnons.' },
      { to: '/reseau-sts',        label: 'Le réseau STS',          desc: 'Premier réseau d\'artisans selliers-garnisseurs de France.' },
    ],
  },
  {
    title: 'Se former',
    icon: GraduationCap,
    links: [
      { to: '/formations-sur-place',  label: 'Formations à Istres',   desc: '3 parcours (12 à 23 jours) + modules à la carte · présentiel.' },
      { to: '/formations-en-ligne',   label: 'E-learning (bientôt)',   desc: 'Vidéos par module, premier épisode offert.' },
      { to: '/financements',          label: 'Financements',           desc: 'France Travail, FAFCEA, OPCO, AGEFIPH, PTP, autofinancement.' },
      { to: '/faq',                   label: 'FAQ',                    desc: 'Toutes les réponses aux questions fréquentes.' },
    ],
  },
  {
    title: 'Contenus & SEO',
    icon: Newspaper,
    links: [
      { to: '/blog',                  label: 'Blog & savoir-faire',   desc: 'Articles longs sur la sellerie, le réemploi et le métier.' },
    ],
  },
  {
    title: 'Entrer en contact',
    icon: Mail,
    links: [
      { to: '/contact',               label: 'Contact',                desc: 'Téléphone, email, formulaire — réponse sous 48h.' },
      { to: '/devenir-apporteur',     label: 'Devenir apporteur',      desc: 'Programme d\'affiliation pour partenaires & professionnels.' },
    ],
  },
  {
    title: 'Espace candidat',
    icon: Users,
    links: [
      { to: '/connexion',             label: 'Connexion',              desc: 'Accède à ton compte élève / candidat.' },
      { to: '/inscription',           label: 'Créer un compte',        desc: 'Ouvre ton espace personnel pour candidater.' },
      { to: '/mes-formations',        label: 'Mes formations',         desc: 'Tableau de bord élève · progression & sessions.' },
      { to: '/mon-dossier',           label: 'Mon dossier',            desc: 'Pipeline de candidature & documents.' },
    ],
  },
  {
    title: 'Légal & confiance',
    icon: ScrollText,
    links: [
      { to: '/mentions-legales',      label: 'Mentions légales',       desc: 'Éditeur, hébergement, propriété intellectuelle.' },
      { to: '/cgv',                   label: 'CGV',                    desc: 'Conditions générales de vente des formations.' },
      { to: '/confidentialite',       label: 'Confidentialité (RGPD)', desc: 'Données personnelles et droits associés.' },
    ],
  },
];

export const PlanDuSitePage = () => {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    document.title = 'Plan du site — Sellerie Académie';
    let mounted = true;
    (async () => {
      try {
        const data = await api.listBlogPosts({ category: 'all', limit: 60 });
        if (mounted) setPosts(data || []);
      } catch (_) { /* silent */ }
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F0]" data-testid="plan-du-site-page">
      {/* Hero */}
      <section className="bg-[#1F2A4A] text-white pt-32 pb-16 lg:pt-40 lg:pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-4">NAVIGATION GLOBALE</p>
          <h1 className="font-serif text-4xl md:text-6xl font-light leading-[1.02] tracking-tight max-w-4xl">
            Plan <span className="italic text-[#C9B896]">du site.</span>
          </h1>
          <p className="mt-6 text-lg text-white/75 max-w-2xl font-light leading-relaxed">
            Une vue d'ensemble de toutes les pages publiques de Sellerie Académie — formations,
            blog, espace candidat et informations légales. Pratique pour parcourir le site en quelques secondes.
          </p>
        </div>
      </section>

      {/* Sections grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-14">
          {SECTIONS.map((sec, i) => {
            const Icon = sec.icon;
            return (
              <div key={i} data-testid={`plan-section-${i}`}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#C9B896]/15 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#C9B896]" />
                  </div>
                  <h2 className="font-serif text-2xl text-[#1F2A4A] font-light">{sec.title}</h2>
                </div>
                <ul className="space-y-3 ml-1">
                  {sec.links.map((link) => (
                    <li key={link.to}>
                      <Link
                        to={L(link.to)}
                        className="group block py-2 border-l-2 border-[#1F2A4A]/10 hover:border-[#C9B896] pl-4 -ml-px transition-all"
                        data-testid={`plan-link-${link.to.replace(/\//g, '_')}`}
                      >
                        <p className="text-[#1F2A4A] font-medium text-[15px] group-hover:text-[#C9B896] transition-colors">{link.label}</p>
                        <p className="text-xs text-[#1F2A4A]/60 leading-relaxed mt-0.5">{link.desc}</p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Blog index */}
        {posts.length > 0 && (
          <div className="mt-24 pt-16 border-t border-[#1F2A4A]/15">
            <div className="flex items-center gap-3 mb-7">
              <div className="w-10 h-10 rounded-xl bg-[#C9B896]/15 flex items-center justify-center">
                <BookOpen className="w-4 h-4 text-[#C9B896]" />
              </div>
              <div>
                <h2 className="font-serif text-2xl text-[#1F2A4A] font-light">Tous les articles du blog</h2>
                <p className="text-xs text-[#1F2A4A]/55 mt-0.5">{posts.length} publication{posts.length > 1 ? 's' : ''}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
              {posts.map((p) => (
                <Link
                  key={p.slug}
                  to={L(`/blog/${p.slug}`)}
                  className="group flex items-start gap-3 py-2 hover:text-[#C9B896] transition-colors"
                  data-testid={`plan-blog-${p.slug}`}
                >
                  <span className="text-[#C9B896] mt-1 text-xs">→</span>
                  <span className="text-[#1F2A4A] text-sm leading-snug group-hover:text-[#C9B896] transition-colors">{p.title}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* SEO sitemap.xml hint */}
        <div className="mt-20 p-8 rounded-2xl bg-white border border-[#1F2A4A]/10 flex items-start gap-5">
          <div className="w-11 h-11 rounded-full bg-[#C9B896]/20 flex items-center justify-center shrink-0">
            <Map className="w-5 h-5 text-[#C9B896]" />
          </div>
          <div>
            <p className="font-serif text-xl text-[#1F2A4A] mb-2">Pour les moteurs de recherche</p>
            <p className="text-sm text-[#1F2A4A]/70 leading-relaxed">
              Un <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="text-[#1F2A4A] underline underline-offset-4 hover:text-[#C9B896]">sitemap.xml</a> dynamique
              est publié pour faciliter l'indexation des articles de blog et des pages de formation.
              Robots et crawlers : voir aussi <a href="/robots.txt" className="text-[#1F2A4A] underline underline-offset-4 hover:text-[#C9B896]">/robots.txt</a>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlanDuSitePage;
